import styled from '@emotion/styled';
import {
    Label,
    CheckBox,
    Button,
    Heading4,
    ButtonWithIcon
} from '@blueprint/ui';
import { css } from '@emotion/css';

const paddingTop = '4.25rem';
const tempLargerBreakPoint = '@media only screen and (min-width: 1140px)';

export const MobileFiltersButton = styled(ButtonWithIcon)(
    ({ theme: { spacing } }) => css`
        padding: ${spacing['4px']} 1.3125rem ${spacing['4px']} 1.3125rem;
        margin-right: ${spacing['8px']};
        min-width: unset;
    `
);

export const DesktopFiltersButton = styled(ButtonWithIcon)(`
        
        display: none;
        
        ${tempLargerBreakPoint} {
            display: flex;
        }        

    `);

export const FiltersContainer = styled.div(
    ({ theme: { mq, spacing } }) => css`
        height: 100%;
        width: 100%;
        max-width: inherit;

        ${mq.small} {
            height: calc(100% - ${spacing['48px']});
            overflow: hidden;
        }
        ${mq.large} {
            height: 100%;
        }
    `
);

export const FiltersHeader = styled('header')(
    ({ theme: { colors, spacing, mq } }) => css`
        border-bottom: solid 1px ${colors['graphic-divider']};
        padding: 0 ${spacing['16px']};
        height: 3rem;
        position: sticky;
        z-index: 6;
        top: 0;
        background-color: ${colors.white};

        ${mq.medium} {
            padding: 0 ${spacing['104px']};
        }

        ${mq.large} {
            height: 5rem;
        }
    `
);

export const Heading = styled(Heading4)(
    () => css`
        position: relative;
        text-align: center;
        top: 50%;
        transform: translateY(-50%);
    `
);

export const LargeHeadingText = styled.div(
    ({ theme: { fontSizes, mq } }) => css`
        font-size: ${fontSizes[8]};
        display: none;

        ${mq.large} {
            display: block;
        }
    `
);

export const SmallHeadingText = styled.div(
    ({ theme: { fontSizes, mq } }) => css`
        font-size: ${fontSizes[2]};

        ${mq.large} {
            display: none;
        }
    `
);

export const ResetLinkButton = styled(Button)(
    ({ theme: { fontSizes, mq, fontWeight } }) => css`
        font-weight: ${fontWeight['bold']};
        font-size: ${fontSizes[1]};
        position: absolute;
        padding: 0;
        top: 50%;
        left: 0;
        transform: translateY(-50%);
        min-width: unset;

        ${mq.large} {
            font-size: ${fontSizes[2]};
        }

        &:hover,
        &:focus {
            transform: translateY(-50%);
        }
    `
);

export const ModalCloseIconButton = styled.button(
    () => css`
        appearance: none;
        padding: 0;
        border: none;
        background-color: transparent;
        cursor: pointer;
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
    `
);

export const FiltersForm = styled.form(
    ({ theme: { spacing, mq } }) => css`
        position: relative;
        width: 100%;
        padding: 0 0 ${spacing['8px']} 0;

        ${mq.small} {
            padding: 0 0 ${spacing['64px']} 0;
            height: 100%;
            overflow: auto;
        }
    `
);

export const FiltersFormSection = styled.div(
    ({ theme: { spacing, mq } }) => css`
        padding: 0 ${spacing['16px']};

        ${mq.medium} {
            padding: 0 ${spacing['104px']};
        }

        &:last-of-type {
            padding-bottom: ${spacing['24px']};
        }
    `
);

export const FiltersAccordionSection = styled.div(
    ({ theme: { spacing, mq } }) => css`
        padding: 0;
        margin-top: ${spacing['8px']};

        ${mq.small} {
            padding: 0 ${spacing['16px']};
        }

        ${mq.medium} {
            padding: 0 ${spacing['104px']};
        }
    `
);

export const FiltersLabel = styled(Label)(
    ({ theme: { spacing, colors } }) => css`
        padding: 0 0 ${spacing['8px']} 0;
        margin-top: ${spacing['24px']};
        display: block;
        margin-bottom: 0;
        color: ${colors['text-body']};
    `
);

export const FiltersFooter = styled.footer(
    ({ theme: { colors, spacing, mq } }) => css`
        padding: ${spacing['8px']} ${spacing['16px']} 10rem ${spacing['16px']};
        background: ${colors.white};
        width: 100%;
        bottom: 0;
        z-index: 2;
        box-shadow: 0px -5px 13px ${colors['graphic-divider']};

        ${mq.small} {
            padding: ${spacing['8px']} ${spacing['16px']};
            position: sticky;
            bottom: 0;
            right: 0;
            border-top: solid 1px ${colors['graphic-divider']};
        }

        ${mq.medium} {
            padding: ${spacing['8px']} ${spacing['104px']};
        }
    `
);

export const FiltersOptionsContainer = styled.div(
    ({ theme: { spacing, mq } }) => css`
        padding: ${paddingTop} ${spacing['16px']} ${spacing['8px']}
            ${spacing['16px']};

        ${mq.small} {
            padding: ${spacing['80px']} ${spacing['104px']} 108rem
                ${spacing['104px']};
        }
    `
);

export const FiltersCheckBox = styled(CheckBox)(
    ({ disabled = false, theme: { spacing, mq } }) => css`
        margin: 0;

        label {
            padding: 0.25rem 0 0.25rem;

            span + span {
                padding-left: 0.5rem;
            }
        }

        .checkbox_icon_wrapper {
            transform: scale(0.833);
        }

        &:last-child {
            margin-bottom: ${spacing['24px']};
        }

        ${mq.small} {
            margin: 0;
        }

        opacity: ${disabled ? 0.5 : 1};
    `
);

export const SometimesInlineFiltersCheckBox = styled(FiltersCheckBox)(
    ({ theme: { mq } }) => css`
        ${mq.small} {
            width: 50%;
            display: inline-block;
            vertical-align: top;
        }
    `
);

export const FiltersAccordionFlexSection = styled.div(
    ({ theme: { spacing, mq } }) => css`
        display: block;

        > div {
            ${FiltersCheckBox} {
                &:last-child {
                    margin-bottom: ${spacing['8px']};
                }
            }
        }

        ${mq.small} {
            display: flex;

            > div {
                flex-grow: 1;
                flex-basis: 0;

                &:first-of-type {
                    margin-right: ${spacing['8px']};
                }

                &:last-of-type {
                    margin-left: ${spacing['8px']};
                }

                ${FiltersCheckBox} {
                    &:last-child {
                        margin-bottom: ${spacing['24px']};
                    }
                }
            }
        }

        ${FiltersCheckBox} {
            ${mq.small} {
                label {
                    padding-top: 0;
                }

                input,
                .checkbox_icon_wrapper {
                    top: 0;
                }
            }
        }
    `
);

export const RadioButtonGroup = styled.div(
    ({ theme: { spacing } }) => css`
        margin-bottom: ${spacing['24px']};
    `
);

export const SometimesInlineRadioWrapper = styled.div(
    ({ theme: { mq } }) => css`
        ${mq.small} {
            width: 50%;
            display: inline-block;
            vertical-align: top;
        }
    `
);

type StyledButtonProps = {
    isDisabled?: boolean;
};

export const UpdateButton = styled(Button)<StyledButtonProps>(
    ({ isDisabled = false }) => css`
        opacity: ${isDisabled ? 0.4 : 1};
        cursor: ${isDisabled ? 'default' : 'pointer'};
    `
);

export const SectionIsHiddenOnDesktop = styled.div(
    ({ theme: { mq } }) => css`
        display: block;

        ${mq.large} {
            display: none;
        }
    `
);
